import * as enabledApi from '@shared/api/enabledPlayApi';
import { CommandType, GetCommandType, hotkeyPrefix } from '@shared/constants/CommandValues';
import { PROFILE_NAME_INPUT_ID } from '@shared/constants/ContentIdValues';
import { KeybindingViewModelFromModel, getDefaultKeyMapping } from '@shared/constants/Keybindings';
import { DefaultVoiceLocale } from '@shared/constants/localization/LocalizationConstants';
import { getDefaultDictationCommands, getSupportedLocale } from '@shared/hooks/helpers/LocalizationHelpers';
import i18n from '@shared/locales/i18n';
import { AudioEventDisplayModel } from '@shared/models/deviceProfiles/AudioEventModel';
import DeviceProfileConfiguration from '@shared/models/deviceProfiles/DeviceProfileConfiguration';
import DeviceProfileCreateUpdateModel from '@shared/models/deviceProfiles/DeviceProfileCreateUpdateModel';
import HotkeyModel from '@shared/models/deviceProfiles/HotkeyModel';
import { IEditProfileContainer } from '@shared/models/deviceProfiles/IEditProfileContainer';
import { KeyCommandDisplayMapping } from '@shared/models/deviceProfiles/KeyCommandMapping';
import { MacroDisplayCommandModel, MacroDisplayModel } from '@shared/models/deviceProfiles/MacroModel';
import UserDeviceProfileModel from '@shared/models/deviceProfiles/UserDeviceProfileModel';
import { AvailableCustomControlsModel } from '@shared/models/devices/AvailableCustomControlsModel';
import { ProfileConflictItem, ProfileConflictModel } from '@shared/models/profiles/ProfileConflictModel';
import IResult from '@shared/models/result/IResult';
import UnexpectedResult from '@shared/models/result/UnexpectedResult';
import _ from 'lodash';
import { useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { publish } from '../helpers/EventCallback';
import { getMacroForHotkey } from '../helpers/HotkeyHelpers';
import {
    getAggregateKeybindings,
    getConflictingKeybindingsVoiceCommandsWithDictation,
    getConflictingMacroVoiceCommandsWithDictation,
    getDuplicateAssociatedPrograms,
    getDuplicateKeybindingCommands,
    getDuplicateMacroCommands,
    getDuplicateMacroNames,
    getEmptyEvents,
    getEmptyKeybindingCommands,
    getEmptyMacroCommands,
    getUnreleasedLatchedKeys,
    keybindsFromDisplayModel,
} from '../helpers/profileUtils';
import useGlobalEditProfileConflicts from '../useGlobalEditProfileConflicts';
import { useProfilesStore } from './ProfilesContainer';

type GlobalConflictsGetter = ReturnType<typeof useGlobalEditProfileConflicts>;
let globalConflictsGetter: GlobalConflictsGetter | null = null;

export const useEditProfileStore = create<IEditProfileContainer>()(
    immer((set, get) => {
        const t = i18n.t.bind(i18n);

        // ---- Helpers ----
        const addNewProfileConflictModel = (
            title: string,
            items: ProfileConflictItem[],
            conflictList: ProfileConflictModel[],
            subtitle?: string
        ) => {
            if (items.length > 0) {
                conflictList.push({ title, items, subtitle });
            }
        };

        const getEditProfilePath = () => {
            const orgId = get().organizationId;
            if (orgId && get().id) {
                return `/org/${orgId}/profiles/edit/${get().id}`;
            }
            if (orgId && !get().id) {
                return ``;
            }
            return `/profiles/edit/${get().id}`;
        };

        const isDuplicateName = (inputName?: string): boolean => {
            const benchmarkName = inputName ?? get().name;
            const duplicateNames = getDuplicateProfiles(benchmarkName);
            if (duplicateNames[0]?.id === get().id) return false;
            return duplicateNames.length > 0;
        };

        const getDuplicateProfiles = (inputName: string): UserDeviceProfileModel[] => {
            return useProfilesStore.getState().profiles.filter((p) => p.name === inputName);
        };

        const getPublicProfileNameIfDuplicate = (inputName?: string): string => {
            const defaultName = inputName ?? get().name;
            const exactDuplicates = getDuplicateProfiles(defaultName);
            if (exactDuplicates.length > 0) {
                const existingDuplicates = useProfilesStore
                    .getState()
                    .profiles.filter((p) => p.name.startsWith(`${defaultName} #`));
                if (existingDuplicates.length > 0) {
                    const lastProfileNumber = Math.max(
                        ...existingDuplicates.map((p) => {
                            const lastChar = p.name.at(-1);
                            return lastChar ? parseInt(lastChar) : NaN;
                        })
                    );
                    return `${defaultName} #${lastProfileNumber + 1}`;
                }
                return `${defaultName} #1`;
            }
            return defaultName;
        };

        const getProfileConfigFromState = (): DeviceProfileConfiguration => {
            const state = get();
            return {
                commandKeyMappings: keybindsFromDisplayModel(state.commandKeyMappings),
                macros: state.macros.map((m) => ({
                    ...m,
                    id: m.id ?? uuidv4(),
                    commands: m.commands.map((c) => c.command),
                })),
                hotkeys: state.hotkeys,
                audioEvents: state.audioEvents.map((a) => ({
                    ...a,
                    commands: a.commands.map((c) => c.command),
                })),
                dictationCommands: state.dictationCommands,
                suggestedGeneratePrompts: state.suggestedGeneratePrompts,
                suggestedRewritePrompts: state.suggestedRewritePrompts,
                associatedPrograms: state.associatedPrograms,
                profileType: 'Default',
            };
        };

        const validateProfileName = (conflictErrors: ProfileConflictModel[]) => {
            let title = '';
            let desc = '';
            let validName = true;
            const name = get().name;
            if (name === '') {
                title = t('common:profile.conflictIndicators.emptyNameTitle');
                desc = t('common:profile.conflictIndicators.emptyNameDescription');
                validName = false;
            } else if (isDuplicateName()) {
                title = t('common:profile.conflictIndicators.duplicateNameTitle');
                desc = t('common:profile.conflictIndicators.duplicateNameDescription');
                validName = false;
            }
            if (!validName) {
                addNewProfileConflictModel(
                    t('common:profile.conflictIndicators.profileNameErrors'),
                    [
                        {
                            displayString: title,
                            key: 'ProfileNameConflict',
                            scrollID: PROFILE_NAME_INPUT_ID,
                            pageLink: getEditProfilePath(),
                        },
                    ],
                    conflictErrors,
                    desc
                );
            }
        };

        const validateProfileItems = (conflictErrors: ProfileConflictModel[]) => {
            const s = get();
            addNewProfileConflictModel(
                t('common:profile.conflictIndicators.emptyControlsMapped'),
                getEmptyMacroCommands({ macros: s.macros, profileId: s.id, t }),
                conflictErrors,
                t('common:profile.conflictIndicators.emptyControlsMappedDescription')
            );
            addNewProfileConflictModel(
                t('common:profile.conflictIndicators.duplicateInputCommandMacroTitle'),
                getDuplicateMacroCommands({ macros: s.macros, profileId: s.id, t }),
                conflictErrors,
                t('common:profile.conflictIndicators.duplicateCommandsDescription')
            );
            addNewProfileConflictModel(
                t('common:profile.conflictIndicators.macrosReleaseKeysMissingTitle'),
                getUnreleasedLatchedKeys({ macros: s.macros, profileId: s.id, defaultLocale: s.defaultLocale, t }),
                conflictErrors,
                t('common:profile.conflictIndicators.macrosReleaseKeysMissingDescription')
            );
            addNewProfileConflictModel(
                t('common:profile.conflictIndicators.macrosVoiceCommandConflictWithDictationTitle'),
                getConflictingMacroVoiceCommandsWithDictation({
                    macros: s.macros,
                    dictationCommands: s.dictationCommands,
                    profileId: s.id,
                    t,
                }),
                conflictErrors,
                t('common:profile.conflictIndicators.macrosVoiceCommandConflictWithDictationDescription')
            );
            addNewProfileConflictModel(
                t('common:profile.conflictIndicators.emptyKeybindingControlsMapped'),
                getEmptyKeybindingCommands({ commandKeyMappings: s.commandKeyMappings, profileId: s.id, t }),
                conflictErrors,
                t('common:profile.conflictIndicators.emptyKeybindingControlsMappedDescription')
            );
            addNewProfileConflictModel(
                t('common:profile.conflictIndicators.duplicateInputCommandBindingsTitle'),
                getDuplicateKeybindingCommands({ commandKeyMappings: s.commandKeyMappings, profileId: s.id, t }),
                conflictErrors,
                t('common:profile.conflictIndicators.duplicateCommandsDescription')
            );
            addNewProfileConflictModel(
                t('common:profile.conflictIndicators.keybindingsVoiceCommandConflictWithDictationTitle'),
                getConflictingKeybindingsVoiceCommandsWithDictation({
                    commandKeyMappings: s.commandKeyMappings,
                    dictationCommands: s.dictationCommands,
                    profileId: s.id,
                    t,
                }),
                conflictErrors,
                t('common:profile.conflictIndicators.keybindingsVoiceCommandConflictWithDictationDescription')
            );
            addNewProfileConflictModel(
                t('common:profile.conflictIndicators.duplicateAssociatedProgramNames'),
                getDuplicateAssociatedPrograms({ associatedPrograms: s.associatedPrograms, profileId: s.id }),
                conflictErrors,
                t('common:profile.conflictIndicators.duplicateAssociatedProgramsDescription')
            );
        };

        const detectConflictWarnings = (conflictWarnings: ProfileConflictModel[]) => {
            const s = get();
            if (!s.id) return;
            if (globalConflictsGetter) {
                const { deviceCommandConflictDisplay, globalCommandConflictDisplay } = globalConflictsGetter(
                    s.macros,
                    s.dictationCommands,
                    s.commandKeyMappings,
                    s.id
                );
                if (globalCommandConflictDisplay?.length > 0) {
                    addNewProfileConflictModel(
                        t('common:customControls.conflictIndicators.globalControls'),
                        globalCommandConflictDisplay,
                        conflictWarnings,
                        t('common:customControls.conflictIndicators.description')
                    );
                }
                if (deviceCommandConflictDisplay?.length > 0) {
                    addNewProfileConflictModel(
                        t('common:customControls.conflictIndicators.deviceControls'),
                        deviceCommandConflictDisplay,
                        conflictWarnings,
                        t('common:customControls.conflictIndicators.description')
                    );
                }
            }
            addNewProfileConflictModel(
                t('common:profile.conflictIndicators.duplicateMacroNames'),
                getDuplicateMacroNames({ macros: s.macros, profileId: s.id, t }),
                conflictWarnings,
                t('common:profile.conflictIndicators.duplicateMacroNamesDescription')
            );
            addNewProfileConflictModel(
                t('common:profile.conflictIndicators.emptyEvent.title'),
                getEmptyEvents({ macros: s.macros, profileId: s.id, t }),
                conflictWarnings,
                t('common:profile.conflictIndicators.emptyEvent.description')
            );
            set((state) => {
                state.profileConflictWarnings = conflictWarnings;
            });
        };

        const isValidProfile = (): boolean => {
            const conflictErrors: ProfileConflictModel[] = [];
            validateProfileName(conflictErrors);
            validateProfileItems(conflictErrors);
            set((state) => {
                state.profileConflictErrors = conflictErrors;
            });

            const audioEventsWithEmptyCommands = get().audioEvents.filter((a) => a.commands.some((c) => !c.command));
            if (audioEventsWithEmptyCommands.length > 0) {
                publish('alertError', {
                    message: t('common:profile.error.audioEventsWithEmptyCommands'),
                    timeout: 5000,
                });
                return false;
            }

            if (conflictErrors.length > 0) {
                publish('alertError', { message: t('common:profile.error.conflicts'), timeout: 5000 });
            }
            return conflictErrors.length === 0;
        };

        const getProfileCreateUpdateModel = (): DeviceProfileCreateUpdateModel => {
            const s = get();
            return {
                name: s.name,
                description: s.description ?? '',
                profileType: 'Default',
                configuration: getProfileConfigFromState(),
                defaultLocale: s.defaultLocale,
                originatedByProfileId: s.originatedByProfileId,
                requiresEmployeeAccess: s.originalProfile?.requiresEmployeeAccess ?? false,
                requiresStudentAccess: s.originalProfile?.requiresStudentAccess ?? false,
                requiresGenericLicense: s.originalProfile?.requiresGenericLicense ?? false,
                requiresPlusAccess: s.originalProfile?.requiresPlusAccess ?? false,
                platform: s.originalProfile?.platform ?? 'generic',
                interests: s.originalProfile?.interests ?? [],
                organizationId: s.organizationId ?? undefined,
            };
        };

        const saveChanges = async (): Promise<IResult<UserDeviceProfileModel>> => {
            if (!isValidProfile()) return new UnexpectedResult<UserDeviceProfileModel>();
            const conflictWarnings: ProfileConflictModel[] = [];
            detectConflictWarnings(conflictWarnings);
            const profileCreateUpdateModel = getProfileCreateUpdateModel();
            const s = get();
            const result = s.id
                ? await enabledApi.updateProfile(s.id, profileCreateUpdateModel)
                : await enabledApi.createProfile(profileCreateUpdateModel);
            if (result?.resultType === 'Ok' && result.data) {
                set((state) => {
                    state.hasChanges = false;
                });
                if (conflictWarnings.length > 0) {
                    publish('alertWarning', { message: t('common:profile.warning'), timeout: 5000 });
                    result.resultType = 'OkWithWarnings';
                } else {
                    publish('alertSuccess', { message: t('common:profile.success.save'), timeout: 5000 });
                }

                if (!s.id) {
                    publish('userProfileAdded', { profile: result.data });
                    set((state) => {
                        state.id = result.data!.id;
                    });
                } else {
                    publish('userProfileUpdated', { profile: result.data });
                }
            } else {
                publish('alertError', { message: t('common:profile.error.save'), timeout: 5000 });
            }
            return result;
        };

        const setFilter = (filter: string) =>
            set((state) => {
                state.filter = filter;
            });

        const setHasChanges = (hasChanges: boolean) =>
            set((state) => {
                state.hasChanges = hasChanges;
            });

        const setId = (id: string | undefined) =>
            set((state) => {
                state.id = id;
            });

        const setIsDeprecatedHotkey = (v: boolean) =>
            set((state) => {
                state.isDeprecatedHotkey = v;
            });

        const setIsDefaultBindingsIncluded = (v: boolean) =>
            set((state) => {
                state.isDefaultBindingsIncluded = v;
            });

        const setIsPreview = (v: boolean) =>
            set((state) => {
                state.isPreview = v;
            });

        const setIsConflictsAcknowledged = (v: boolean) =>
            set((state) => {
                state.isProfileConflictsAcknowledged = v;
            });

        const setAvailableCustomControls = (controls: AvailableCustomControlsModel[]) =>
            set((state) => {
                state.availableCustomControls = controls;
            });

        const setOrganizationId = (id: string | null) =>
            set((state) => {
                state.organizationId = id;
            });

        const updateAssociatedPrograms = (programs: string[]) =>
            set((state) => {
                state.associatedPrograms = programs;
            });

        const updateAudioEvents = (events: AudioEventDisplayModel[]) =>
            set((state) => {
                state.audioEvents = events;
            });

        const updateCommandKeyMappings = (keybindings: KeyCommandDisplayMapping[]) =>
            set((state) => {
                state.commandKeyMappings = keybindings;
            });

        const resetKeybindingsToDefault = () => {
            const { originalProfile, defaultLocale } = get();
            const defaultKeyBindings = getDefaultKeyMapping(defaultLocale, originalProfile?.platform);
            set((state) => {
                state.commandKeyMappings = defaultKeyBindings;
                state.hasChanges = true;
            });
        };

        const resetKeybindingsToEmpty = () => {
            const { originalProfile, defaultLocale } = get();
            const defaultKeyBindings = getDefaultKeyMapping(defaultLocale, originalProfile?.platform);
            const emptyKeyBindings = defaultKeyBindings.map((k) => ({ ...k, commands: [] }));
            set((state) => {
                state.commandKeyMappings = emptyKeyBindings;
                state.hasChanges = true;
            });
        };

        const updateDictationCommands = (commands: string[]) =>
            set((state) => {
                state.dictationCommands = commands;
            });

        const updateSuggestedGeneratePrompts = (prompts: string[]) =>
            set((state) => {
                state.suggestedGeneratePrompts = prompts;
            });

        const updateSuggestedRewritePrompts = (prompts: string[]) =>
            set((state) => {
                state.suggestedRewritePrompts = prompts;
            });

        const updateHotkeys = (hotkeys: HotkeyModel[]) =>
            set((state) => {
                state.hotkeys = hotkeys;
            });

        const updateMacros = (macros: MacroDisplayModel[]) =>
            set((state) => {
                state.macros = macros;
            });

        const updateName = (name: string) =>
            set((state) => {
                state.name = name;
            });

        const updateDescription = (description: string) =>
            set((state) => {
                state.description = description;
            });

        const handleLocaleChange = (value: string) =>
            set((state) => {
                state.defaultLocale = value;
                state.hasChanges = true;
            });

        const getClonedProfile = (
            profile: UserDeviceProfileModel,
            originatedById: string | undefined,
            nameOverride?: string
        ) => {
            const clonedProfile = _.cloneDeep(profile);
            const duplicateName = getPublicProfileNameIfDuplicate(profile.name);
            Object.assign(clonedProfile, {
                profileType: 'Default',
                id: undefined,
                name: nameOverride ?? duplicateName,
                originatedByProfileId: originatedById,
            });
            return clonedProfile;
        };

        const getUserDeviceProfileFromState = () => {
            const s = get();
            const now = new Date();
            const profileConfig: UserDeviceProfileModel = {
                id: s.id,
                name: s.name,
                description: s.description,
                profileType: 'Default',
                configuration: getProfileConfigFromState(),
                createdDate: now.toISOString(),
                modifiedDate: now.toISOString(),
                defaultLocale: s.defaultLocale,
                requiresEmployeeAccess: s.originalProfile?.requiresEmployeeAccess ?? false,
                requiresStudentAccess: s.originalProfile?.requiresStudentAccess ?? false,
                requiresGenericLicense: s.originalProfile?.requiresGenericLicense ?? false,
                requiresPlusAccess: s.originalProfile?.requiresPlusAccess ?? false,
                interests: s.originalProfile?.interests ?? [],
            };
            if (process.platform === 'darwin') profileConfig.platform = 'MacOS';
            else if (process.platform === 'win32') profileConfig.platform = 'Windows';
            return profileConfig;
        };

        const loadPreview = async (shareCode: string) => {
            set((state) => {
                state.isLoading = true;
            });
            const result = await enabledApi.getProfileSharePreview(shareCode);
            if (result?.resultType === 'Ok' && result.data) {
                get().updateProfileState({
                    ...result.data,
                    name: result.data.profileName,
                    id: result.data.id,
                    userId: undefined,
                    profileType: result.data.configuration.profileType,
                    defaultLocale: result.data.defaultLocale,
                    originatedByProfileId: result.data.userDeviceProfileId,
                    interests: [],
                });
                set((state) => {
                    state.isPreview = true;
                });
            } else {
                publish('alertError', { message: t('common:profile.error.load'), timeout: 5000 });
            }
            set((state) => {
                state.isLoading = false;
            });
            return result;
        };

        const loadPublicPreview = async (profileId: string) => {
            set((state) => {
                state.isLoading = true;
            });
            const result = await enabledApi.getDeviceProfile(profileId);
            if (result?.resultType === 'Ok' && result.data) {
                get().updateProfileState({
                    ...result.data,
                    userId: undefined,
                    profileType: result.data.configuration.profileType,
                    originatedByProfileId: result.data.originatedByProfileId,
                });
                set((state) => {
                    state.isPreview = true;
                });
            } else {
                publish('alertError', { message: t('common:profile.error.load'), timeout: 5000 });
            }
            set((state) => {
                state.isLoading = false;
            });
        };

        const loadOrgPreview = async (profileId: string) => {
            set((state) => {
                state.isLoading = true;
            });
            try {
                const result = await enabledApi.getDeviceProfile(profileId ?? '');
                if (result?.resultType === 'Ok' && result.data) {
                    get().updateProfileState({
                        ...result.data,
                        userId: undefined,
                        profileType: result.data.configuration.profileType,
                        originatedByProfileId: result.data.originatedByProfileId,
                    });
                    set((state) => {
                        state.isPreview = true;
                    });
                } else {
                    publish('alertError', { message: t('common:profile.error.load'), timeout: 5000 });
                }
            } catch (error) {
                publish('alertError', { message: t('common:profile.error.load'), timeout: 5000 });
            } finally {
                set((state) => {
                    state.isLoading = false;
                });
            }
        };

        const addPreview = async (shareCode: string) => {
            set((state) => {
                state.isLoading = true;
            });
            const result = await enabledApi.acceptProfileShare(shareCode);
            if (result?.resultType === 'Ok' && result.data) {
                publish('userProfileAdded', { profile: result.data });
                publish('alertSuccess', { message: t('common:profile.success.add'), timeout: 5000 });
            } else {
                publish('alertError', { message: t('common:profile.error.add'), timeout: 5000 });
            }
            set((state) => {
                state.isLoading = false;
            });
            return result;
        };

        const setDefaultProfile = (locale: string) => {
            const language = getSupportedLocale(locale, 'voice') ?? DefaultVoiceLocale;
            let keyBindings = getDefaultKeyMapping(language);
            if (!get().isDefaultBindingsIncluded) {
                keyBindings = keyBindings.map((k) => ({ ...k, commands: [] }));
            }
            set((state) => {
                state.organizationId = null;
                state.defaultLocale = language;
                state.description = undefined;
                state.imageUri = undefined;
                state.hasChanges = false;
                state.name = '';
                state.isDeprecatedHotkey = false;
                state.id = undefined;
                state.macros = [];
                state.hotkeys = [];
                state.dictationCommands = getDefaultDictationCommands(language);
                state.associatedPrograms = [];
                state.audioEvents = [];
                state.commandKeyMappings = keyBindings;
                state.profileConflictErrors = [];
                state.profileConflictWarnings = [];
                state.isProfileConflictsAcknowledged = false;
                state.originatedByProfileId = undefined;
            });
        };

        const updateProfileState = (profile: UserDeviceProfileModel, isInitial?: boolean) => {
            const clonedProfile = _.cloneDeep(profile);
            set((state) => {
                state.originalProfile = clonedProfile;
            });
            if (!profile.configuration) return;

            const language =
                getSupportedLocale(profile.defaultLocale ?? DefaultVoiceLocale, 'voice') ?? DefaultVoiceLocale;
            const {
                hotkeys,
                macros,
                commandKeyMappings: profileKeybindings,
                audioEvents,
                dictationCommands,
                suggestedGeneratePrompts,
                suggestedRewritePrompts,
                associatedPrograms,
            } = profile.configuration;

            const updatedMacros =
                macros?.map((a) => ({
                    ...a,
                    id: a.id ?? uuidv4(),
                    commands: a.commands.map((c) => ({ command: c, commandType: GetCommandType(c) })),
                })) ?? [];

            let isDeprecated = false;
            const updatedHotkeys = (hotkeys ?? []).map((h) => {
                const localId = h.localId ?? uuidv4();
                const macroLocalId = h.macroLocalId ?? getMacroForHotkey(h, updatedMacros)?.m?.id;
                const deprecated = !h.command.startsWith(hotkeyPrefix);
                if (deprecated) isDeprecated = true;
                return {
                    ...h,
                    localId,
                    macroLocalId,
                    ...(deprecated && !h.macroName ? { macroName: 'Deprecated Hotkey' } : {}),
                };
            });

            const updatedAudioEvents = (audioEvents || []).map((a) => ({
                ...a,
                commands: (a.commands || []).map((c) => ({ command: c, commandType: GetCommandType(c) })),
            }));

            let keybindings = getAggregateKeybindings(
                getDefaultKeyMapping(language, profile.platform),
                profileKeybindings?.map((k) => KeybindingViewModelFromModel(k, language, [], profile.platform))
            );
            if (!get().isDefaultBindingsIncluded && !profile.id && !profileKeybindings) {
                keybindings = keybindings.map((k) => ({ ...k, commands: [] }));
            }

            const programs = associatedPrograms ?? [];
            const dictCmds = dictationCommands ?? getDefaultDictationCommands(language);
            const genPrompts = suggestedGeneratePrompts ?? [];
            const rewritePrompts = suggestedRewritePrompts ?? [];

            set((state) => {
                state.defaultLocale = language;
                state.macros = updatedMacros;
                state.isDeprecatedHotkey = isDeprecated;
                state.hotkeys = updatedHotkeys;
                state.audioEvents = updatedAudioEvents;
                state.dictationCommands = dictCmds;
                state.suggestedGeneratePrompts = genPrompts;
                state.suggestedRewritePrompts = rewritePrompts;
                state.associatedPrograms = programs;
                state.id = profile.id;
                state.name = profile.name;
                state.description = profile.description;
                state.imageUri = profile.imageUrl;
                state.originatedByProfileId = profile.originatedByProfileId;
                state.commandKeyMappings = keybindings;
                state.isProfileConflictsAcknowledged = false;
                state.profileConflictErrors = [];
                state.profileConflictWarnings = [];
            });
        };

        const updateDeprecatedHotkey = (hotkey: HotkeyModel, macrosArg: MacroDisplayModel[]) => {
            const newCommand = {
                command: hotkeyPrefix + hotkey.displayName,
                commandType: 'hotkey' as CommandType,
                hotkeyLocalId: hotkey.localId,
            };
            const currentHotkeys = get().hotkeys;
            const macroId = hotkey.macroLocalId ? hotkey.macroLocalId : uuidv4();
            const updatedHotkey = { ...hotkey, command: hotkeyPrefix + hotkey.displayName, macroLocalId: macroId };
            const hotkeyIndex = currentHotkeys.indexOf(hotkey);
            const linkedMacro = macrosArg.find((m) => m.id === hotkey.macroLocalId);
            const updatedMacros = [...macrosArg];
            const updatedHotkeys = [...currentHotkeys];
            if (linkedMacro) {
                const indexOfLinkedMacro = macrosArg.indexOf(linkedMacro);
                updatedMacros[indexOfLinkedMacro] = {
                    ...linkedMacro,
                    commands: [...linkedMacro.commands, newCommand],
                };
                updatedHotkeys[hotkeyIndex] = updatedHotkey;
            } else {
                updatedHotkeys.splice(hotkeyIndex, 1);
            }
            set((state) => {
                state.macros = updatedMacros;
                state.hotkeys = updatedHotkeys;
            });
        };

        const removeHotkeyFromMacro = (command: MacroDisplayCommandModel) => {
            const updated = get().hotkeys.filter((h) => h.command !== command.command);
            set((state) => {
                state.hotkeys = updated;
            });
        };

        const updateHotkeyFromMacro = (
            value: string,
            macro: MacroDisplayModel,
            index: number,
            originalValue: string
        ) => {
            const currentHotkeys = get().hotkeys;
            const matchHotkey = currentHotkeys.find((h) => h.command === originalValue);
            if (matchHotkey) {
                const hotkeyIndex = currentHotkeys.indexOf(matchHotkey);
                const updatedHotkey = {
                    ...matchHotkey,
                    displayName: value.replace(hotkeyPrefix, ''),
                    command: value,
                };
                const updated = [...currentHotkeys];
                updated[hotkeyIndex] = updatedHotkey;
                set((state) => {
                    state.hotkeys = updated;
                });
                return matchHotkey.localId ?? '';
            } else {
                return get().addHotkeyCommandForMacro(macro, currentHotkeys, value);
            }
        };

        const addHotkeyCommandForMacro = (macro: MacroDisplayModel, hotkeys: HotkeyModel[], command: string) => {
            const newHotkey = {
                command,
                displayName: command.replace(hotkeyPrefix, ''),
                localId: uuidv4(),
                macroId: macro.id,
                macroName: macro.name,
            };
            set((state) => {
                state.hotkeys = [...hotkeys, newHotkey];
            });
            return newHotkey.localId;
        };

        const resetPreviewStatus = () =>
            set((state) => {
                state.isPreview = false;
            });

        return {
            // State
            associatedPrograms: [],
            audioEvents: [],
            availableCustomControls: [],
            commandKeyMappings: [],
            defaultLocale: DefaultVoiceLocale,
            description: undefined,
            dictationCommands: [],
            errors: [],
            filter: '',
            hasChanges: false,
            hotkeys: [],
            id: undefined,
            imageUri: undefined,
            isDefaultBindingsIncluded: false,
            isDeprecatedHotkey: false,
            isLoading: false,
            isPreview: false,
            isProfileConflictsAcknowledged: false,
            macros: [],
            name: '',
            organizationId: null,
            originalProfile: null,
            originatedByProfileId: undefined,
            profileConflictErrors: [],
            profileConflictWarnings: [],
            suggestedGeneratePrompts: [],
            suggestedRewritePrompts: [],

            // Actions
            saveChanges,
            setFilter,
            setHasChanges,
            setId,
            setIsDeprecatedHotkey,
            setIsDefaultBindingsIncluded,
            setIsPreview,
            setIsConflictsAcknowledged,
            setAvailableCustomControls,
            setOrganizationId,
            updateAssociatedPrograms,
            updateAudioEvents,
            updateCommandKeyMappings,
            resetKeybindingsToDefault,
            resetKeybindingsToEmpty,
            updateDictationCommands,
            updateSuggestedGeneratePrompts,
            updateSuggestedRewritePrompts,
            updateHotkeys,
            updateMacros,
            updateName,
            updateDescription,
            handleLocaleChange,
            getClonedProfile,
            getProfileConfigFromState,
            getPublicProfileNameIfDuplicate,
            getUserDeviceProfileFromState,
            isDuplicateName,
            loadPreview,
            loadPublicPreview,
            loadOrgPreview,
            addPreview,
            setDefaultProfile,
            updateProfileState,
            updateDeprecatedHotkey,
            removeHotkeyFromMacro,
            updateHotkeyFromMacro,
            addHotkeyCommandForMacro,
            resetPreviewStatus,
        };
    })
);

export function useEditProfileContainerSetup() {
    const getGlobalConflicts = useGlobalEditProfileConflicts();
    useEffect(() => {
        globalConflictsGetter = getGlobalConflicts;
        return () => {
            globalConflictsGetter = null;
        };
    }, [getGlobalConflicts]);
}
